const DB_NAME = 'rtk_cache';
const DB_VERSION = 1;
const STORE_NAME = 'queryCache';

export const CACHE_LIFETIME = 1000 * 60 * 60; // 1 hour

export interface CacheEntry {
  data: any;
  timestamp: number;
  cacheKey: string;
  lifetime: number;
}

function promisifyRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

class IndexedDBService {
  private db: IDBDatabase | null = null;

  private initPromise: Promise<void> | null = null;

  private cleanupPromise: Promise<void> | null = null;

  private async ensureConnection(): Promise<void> {
    if (this.db) return;

    if (!this.initPromise) {
      this.initPromise = this.init();
    }

    await this.initPromise;
  }

  init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onblocked = () => {
        reject(new Error('IndexedDB upgrade blocked.'));
      };

      request.onerror = () => {
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;

        // Set up a handler for when the connection unexpectedly closes.
        this.db.onclose = () => {
          this.db = null;
          this.initPromise = null;
        };

        // Perform a non-blocking cleanup of stale cache entries upon connection.
        this.cleanupStaleCache();
        resolve();
      };

      // This event fires only when the DB_VERSION changes or the DB is first created.
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        // Create the object store if it doesn't already exist.
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'cacheKey' });
        }
      };
    });
  }

  public async set(
    cacheKey: string,
    data: any,
    lifetime: number = CACHE_LIFETIME,
  ): Promise<void> {
    await this.ensureConnection();
    if (!this.db) {
      throw new Error('IndexedDB connection not available.');
    }

    const transaction = this.db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const entry: CacheEntry = {
      data,
      timestamp: Date.now(),
      cacheKey,
      lifetime,
    };

    await promisifyRequest(store.put(entry));
  }

  public async get(cacheKey: string): Promise<CacheEntry | null> {
    await this.ensureConnection();
    if (!this.db) {
      throw new Error('IndexedDB connection not available.');
    }

    const transaction = this.db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(cacheKey);

    const result = await promisifyRequest(request);
    return result || null;
  }

  public async delete(cacheKey: string): Promise<void> {
    await this.ensureConnection();
    if (!this.db) {
      // If there's no DB, we can consider the deletion successful.
      return;
    }

    const transaction = this.db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    await promisifyRequest(store.delete(cacheKey));
  }

  /**
   * Iterates through the cache and removes stale entries.
   * This is designed to be a non-blocking background task.
   */
  public async cleanupStaleCache(): Promise<void> {
    // If a cleanup is already running, don't start another one.
    if (this.cleanupPromise) {
      return this.cleanupPromise;
    }

    const cleanup = async (): Promise<void> => {
      await this.ensureConnection();
      if (!this.db) return;

      // eslint-disable-next-line consistent-return
      return new Promise<void>((resolve, reject) => {
        const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const cursorRequest = store.openCursor();

        // Handle transaction-level errors.
        transaction.onerror = () => reject(transaction.error);
        transaction.oncomplete = () => resolve();

        cursorRequest.onsuccess = () => {
          const cursor = cursorRequest.result;
          if (cursor) {
            const entry: CacheEntry = cursor.value;
            const entryLifetime = entry.lifetime || CACHE_LIFETIME;
            // Check if the entry has expired.
            if (Date.now() - entry.timestamp > entryLifetime) {
              cursor.delete();
            }
            cursor.continue();
          }
        };
      });
    };

    this.cleanupPromise = cleanup();
    try {
      await this.cleanupPromise;
    } catch (error) {
      // silent ignore
    } finally {
      // Ensure the lock is released, allowing future cleanups.
      this.cleanupPromise = null;
    }
    return undefined;
  }
}

export const indexedDBService = new IndexedDBService();
